/**
 * 조회조건 세션 유지 — [조회] 때 조회영역 값을 `MenuStorage.menuOnSession.setSearch` + URL 에 남기고, 진입 때 URL 에서
 * 한 번 되살린다(rgr01V2 `searchQuery.ts` 방식). 키는 조회영역 값 버스의 키를 그대로 쓴다.
 * `Utils.qsStringify`(comma·encode:false) → `useParsedQuery`(comma) 왕복이라 목록 값은 하나씩 인코딩해 싣는다(화주명 `A,B` 보호).
 */
const LIST_KEYS = ['service_type', 'shipper_name', 'ORD_NO', 'ORD_OPT_NO'] as const
const TEXT_KEYS = ['ORD_TYPE', 'ORD_STATUS'] as const
const RANGES = [
  ['PLN_DATE', 'plnDateFrom', 'plnDateTo'],
  ['ACT_DATE', 'actDateFrom', 'actDateTo'],
] as const
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

type RangeKey = (typeof RANGES)[number][1 | 2]
export type Rgr13SearchQuery = Partial<Record<(typeof LIST_KEYS)[number] | (typeof TEXT_KEYS)[number] | RangeKey, string | string[]>>

const cleanList = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' || typeof x === 'number').map(String).filter(Boolean)
  return typeof v === 'string' && v.trim() ? [v.trim()] : []
}
const asText = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined)

/** 조회영역 값 → 저장 형태. 빈 값은 싣지 않는다 */
export const toSearchQuery = (fl: Record<string, unknown>): Rgr13SearchQuery => {
  const q: Rgr13SearchQuery = {}
  LIST_KEYS.forEach((k) => {
    const list = cleanList(fl[k])
    if (list.length) q[k] = list.map(encodeURIComponent)
  })
  RANGES.forEach(([k, fromKey, toKey]) => {
    const [from, to] = Array.isArray(fl[k]) ? (fl[k] as unknown[]) : []
    if (asText(from) && asText(to)) Object.assign(q, { [fromKey]: from, [toKey]: to })
  })
  TEXT_KEYS.forEach((k) => {
    if (asText(fl[k])) q[k] = fl[k] as string
  })
  return q
}

/**
 * URL 쿼리 → 조회영역 값. 화주 조건(화주유형·화주명)이 없으면 복원하지 않는다(그런 조건은 [조회] 가 막아 저장되지 않는다 — 최초 진입).
 * 형식이 어긋난 날짜는 버린다 — 입고예정일자가 빠지면 조회영역엔 기본값(오늘)이 보이지만 진입 조회는 하지 않는다(toSearchCondition 이 막는다).
 */
export const fromSearchQuery = (q: Record<string, unknown>): Record<string, unknown> | undefined => {
  const lists = Object.fromEntries(LIST_KEYS.map((k) => [k, cleanList(q[k])]))
  if (!lists.service_type.length && !lists.shipper_name.length) return undefined
  const fl: Record<string, unknown> = {
    // 화주유형·화주명 '전체' 는 [''] 다 — 비어 있으면 셀렉트가 '전체' 를 가리키게 한다(화주명이 undefined 면 filterFallback 이 세션 화주로 채운다)
    service_type: lists.service_type.length ? lists.service_type : [''],
    shipper_name: lists.shipper_name.length ? lists.shipper_name : [''],
  }
  ;(['ORD_NO', 'ORD_OPT_NO'] as const).forEach((k) => {
    if (lists[k].length) fl[k] = lists[k]
  })
  RANGES.forEach(([k, fromKey, toKey]) => {
    const from = asText(q[fromKey])
    const to = asText(q[toKey])
    if (from && to && DATE_RE.test(from) && DATE_RE.test(to)) fl[k] = [from, to]
  })
  TEXT_KEYS.forEach((k) => {
    if (asText(q[k])) fl[k] = q[k]
  })
  return fl
}
